import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { API_STRING as apiString, api } from '@/App';
import { toast } from 'sonner';


import { useDarkMode } from '@/hooks/useDarkMode';

import { logger } from '@/utils/logger';
import { Check, CheckCheck, Play, Search, Settings, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import EmptyState from '@/components/EmptyState';
export default function Notifications({ user }) {
  const navigate = useNavigate();
  const darkMode = useDarkMode();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  // Saved-search management lives in a modal (opened from the "Manage alerts"
  // button in the header), saved-search matches still fire as regular
  // notifications in the main inbox list.
  const [manageOpen, setManageOpen] = useState(false);
  const [searches, setSearches] = useState([]);
  const [searchesLoading, setSearchesLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchNotifications();
  }, [user, navigate]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      // Always fetch the full list so the "All (N)" and "Unread (N)" counts
      // stay accurate regardless of which filter tab is currently selected.
      const response = await fetch(`${apiString}/notifications`, {
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${apiString}/notifications/${notificationId}/read`, {
        method: 'PATCH',
      });

      if (response.ok) {
        setNotifications(notifications.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        ));
      }
    } catch (error) {
      logger.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch(`${apiString}/notifications/mark-all-read`, {
        method: 'POST',
      });

      if (response.ok) {
        toast.success('All notifications marked as read');
        fetchNotifications();
      }
    } catch (error) {
      logger.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const response = await fetch(`${apiString}/notifications/${notificationId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setNotifications(notifications.filter(n => n.id !== notificationId));
        toast.success('Notification deleted');
      }
    } catch (error) {
      logger.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // ---- Saved search alerts (merged from the old /my-alerts page) ----
  const fetchSearches = async () => {
    setSearchesLoading(true);
    try {
      const response = await api.get('/saved-searches');
      setSearches(response.data);
    } catch (error) {
      logger.error('Failed to fetch saved searches:', error);
      toast.error('Failed to load your alerts');
    } finally {
      setSearchesLoading(false);
    }
  };

  useEffect(() => {
    // Lazy-load the saved searches only when the user opens the manage
    // modal for the first time. Keeps the initial /notifications render fast.
    if (manageOpen && searches.length === 0 && !searchesLoading) {
      fetchSearches();
    }
  }, [manageOpen, searches.length, searchesLoading]);

  const deleteSearch = async (searchId) => {
    try {
      await api.delete(`/saved-searches/${searchId}`);
      setSearches(searches.filter(s => s.id !== searchId));
      toast.success('Alert deleted');
    } catch (error) {
      logger.error('Failed to delete search:', error);
      toast.error('Failed to delete alert');
    }
  };

  const toggleSearchActive = async (search) => {
    try {
      await api.put(`/saved-searches/${search.id}`, { is_active: !search.is_active });
      setSearches(searches.map(s => s.id === search.id ? { ...s, is_active: !s.is_active } : s));
      toast.success(search.is_active ? 'Alert paused' : 'Alert activated');
    } catch (error) {
      logger.error('Failed to update search:', error);
      toast.error('Failed to update alert');
    }
  };

  const testSearch = async (searchId) => {
    try {
      const response = await api.post(`/saved-searches/${searchId}/test`);
      toast.success(`Found ${response.data.count} matching products!`);
    } catch (error) {
      logger.error('Failed to test search:', error);
      toast.error('Failed to test alert');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    const iconStyle = { color: '#9E6752' };
    switch (type) {
      case 'occasion_reminder':
        return '🎂';
      case 'welcome':
        return '🎁';
      case 'marketing':
        return '✨';
      default:
        return '📬';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  // Client-side filtering so we can keep the full list in state (and thus
  // show accurate totals in the All/Unread pills) while only rendering the
  // subset the user selected.
  const visibleNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  return (
    <div className="min-h-screen transition-colors page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex items-start justify-between gap-3 flex-wrap page-rise-1">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-3 transition-colors" style={{ color: darkMode.textPrimary }}>
              <Bell className="w-8 h-8" style={{ color: '#9E6752' }} />
              Notifications
            </h1>
            <p className="text-base sm:text-lg transition-colors" style={{ color: darkMode.textSecondary }}>
              Stay updated with your gift reminders and saved-search matches
            </p>
          </div>
          <Button
            onClick={() => setManageOpen(true)}
            variant="outline"
            size="sm"
            data-testid="manage-alerts-button"
            className="flex items-center gap-2 hover:transform-none"
          >
            <Settings className="w-4 h-4" />
            Manage alerts
          </Button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex gap-2">
            <Button
              onClick={() => setFilter('all')}
              size="sm"
              // Fixed width + no hover lift so the button doesn't jump or
              // resize when the count label appears/disappears or selection
              // changes. Overrides App.css's global `button:hover` transform.
              className="justify-center hover:transform-none"
              style={{
                background: filter === 'all' ? '#9E6752' : darkMode.isDark ? 'rgba(158, 103, 82, 0.2)' : '#F3F4F6',
                color: filter === 'all' ? 'white' : darkMode.textPrimary,
                minWidth: '96px',
              }}
            >
              All ({notifications.length})
            </Button>
            <Button
              onClick={() => setFilter('unread')}
              size="sm"
              className="justify-center hover:transform-none"
              style={{
                background: filter === 'unread' ? '#9E6752' : darkMode.isDark ? 'rgba(158, 103, 82, 0.2)' : '#F3F4F6',
                color: filter === 'unread' ? 'white' : darkMode.textPrimary,
                minWidth: '112px',
              }}
            >
              Unread {unreadCount > 0 && `(${unreadCount})`}
            </Button>
          </div>

          {unreadCount > 0 && (
            <Button
              onClick={markAllAsRead}
              size="sm"
              className="flex items-center gap-2"
              style={{
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F3F4F6',
                color: darkMode.textPrimary
              }}
            >
              <CheckCheck className="w-4 h-4" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#9E6752' }}></div>
            </div>
          ) : visibleNotifications.length === 0 ? (
            <EmptyState
              icon={Bell}
              title="All caught up"
              description={"Birthdays, anniversaries, and gift-idea updates will show up here. Save an alert from a Browse search and we\u2019ll ping you when new matches land."}
              cta={{
                label: 'Browse Gifts',
                onClick: () => navigate('/browse'),
                testId: 'notifications-empty-browse-btn',
              }}
              secondaryCta={{
                label: 'Manage alerts',
                onClick: () => { setManageOpen(true); fetchSearches(); },
                testId: 'notifications-empty-alerts-btn',
              }}
              darkMode={darkMode}
              testId="notifications-empty"
            />
          ) : (
            visibleNotifications.map(notification => (
              <div
                key={notification.id}
                className="rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.01]"
                style={{
                  background: notification.read 
                    ? (darkMode.isDark ? 'rgba(45, 67, 84, 0.5)' : '#F9FAFB')
                    : (darkMode.isDark ? '#2D4354' : 'white'),
                  boxShadow: notification.read ? 'none' : '0 2px 10px rgba(0,0,0,0.08)',
                  borderLeft: notification.read ? 'none' : '4px solid #9E6752'
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-sm sm:text-base transition-colors" style={{ color: darkMode.textPrimary }}>
                        {notification.title}
                      </h3>
                      <span className="text-xs transition-colors flex-shrink-0" style={{ color: darkMode.textSecondary }}>
                        {formatDate(notification.created_at)}
                      </span>
                    </div>
                    
                    <p className="text-sm mt-1 transition-colors" style={{ color: darkMode.textSecondary }}>
                      {notification.message}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notification.read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        className="p-2 rounded-lg transition-all hover:scale-110"
                        style={{ background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F3F4F6' }}
                        title="Mark as read"
                      >
                        <Check className="w-4 h-4" style={{ color: '#9E6752' }} />
                      </button>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      className="p-2 rounded-lg transition-all hover:scale-110"
                      style={{ background: darkMode.isDark ? 'rgba(239, 68, 68, 0.1)' : '#FEE2E2' }}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" style={{ color: '#DC2626' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Manage alerts modal, saved-search management (pause/test/delete) */}
        <Dialog open={manageOpen} onOpenChange={setManageOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" style={{ color: '#9E6752' }} />
                Manage saved alerts
              </DialogTitle>
              <DialogDescription>
                Pause, test, or delete the searches you've saved. Matches still
                arrive as notifications in your inbox.
              </DialogDescription>
            </DialogHeader>

            <div data-testid="manage-alerts-body" className="mt-2">
              {searchesLoading ? (
                <div className="text-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: '#9E6752' }}></div>
                </div>
              ) : searches.length === 0 ? (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: darkMode.textSecondary }} />
                  <p className="text-base font-medium mb-1" style={{ color: darkMode.textPrimary }}>
                    No saved alerts yet
                  </p>
                  <p className="text-sm mb-4" style={{ color: darkMode.textSecondary }}>
                    Save searches from the Browse page to get notified when new gifts match.
                  </p>
                  <Button
                    onClick={() => { setManageOpen(false); navigate('/'); }}
                    style={{ background: '#9E6752', color: 'white' }}
                    className="hover:transform-none"
                  >
                    Browse Gifts
                  </Button>
                </div>
              ) : (
                <div className="grid gap-3">
                  {searches.map(search => (
                    <div
                      key={search.id}
                      data-testid={`saved-alert-${search.id}`}
                      className="p-4 rounded-lg border"
                      style={{
                        borderColor: darkMode.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="text-base font-semibold" style={{ color: darkMode.textPrimary }}>
                              {search.name}
                            </h3>
                            <span
                              className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                              style={{
                                background: search.is_active ? 'rgba(16, 185, 129, 0.12)' : 'rgba(156, 163, 175, 0.15)',
                                color: search.is_active ? '#10B981' : '#9CA3AF',
                              }}
                            >
                              {search.is_active ? 'Active' : 'Paused'}
                            </span>
                          </div>
                          <p className="text-xs" style={{ color: darkMode.textSecondary }}>
                            Delivery: {search.notification_preference === 'both'
                              ? 'Email & In-app'
                              : search.notification_preference === 'email'
                                ? 'Email only'
                                : 'In-app only'}
                            {search.last_notified_at && (
                              <> · Last: {new Date(search.last_notified_at).toLocaleDateString()}</>
                            )}
                          </p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => testSearch(search.id)}
                            title="Test this alert"
                            data-testid={`alert-test-${search.id}`}
                            className="hover:transform-none"
                          >
                            <Play className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleSearchActive(search)}
                            data-testid={`alert-toggle-${search.id}`}
                            className="hover:transform-none"
                          >
                            {search.is_active ? 'Pause' : 'Activate'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteSearch(search.id)}
                            title="Delete"
                            data-testid={`alert-delete-${search.id}`}
                            className="text-red-600 hover:bg-red-50 hover:transform-none"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
